define((require) => {
    "use strict";

    var ImprovedModel          = require("shared/zbone/ImprovedModel"),
        Backbone               = require("backbone"),
        _                      = require("underscore"),
        Preconditions          = require("shared/util/Preconditions"),
        RestApi                = require("shared/services/RestApi"),
        SyncManager            = require("shared/services/sync/SyncManager"),
        Permissions            = require("shared/security/Permissions"),
        ProjectAsset           = require("ui/editor/project/ProjectAsset"),
        ProjectAssetCollection = require("ui/editor/project/ProjectAssetCollection");

    /**
     * Represents a project.
     *
     * @class Project
     * @constructor
     */
    return ImprovedModel.extend("Project", {

        defaults: {

            /**
             * The project name.
             *
             * @type {String}
             */
            name: "Untitled Project",

            /**
             * The project assets.
             *
             * @type {ProjectAssetCollection}
             */
            assets: [],

            /**
             * The inferred project assets.
             *
             * @type {ProjectAssetCollection}
             */
            inferredAssets: []
        },

        relations: [
            {
                type: Backbone.Many,
                key: "assets",
                collectionType: ProjectAssetCollection,
                reverseRelation: {
                    key: "project"
                }
            }, {
                type: Backbone.Many,
                key: "inferredAssets",
                collectionType: ProjectAssetCollection
            }],

        // This are collections, so we will update them entirely! We have to update "assets" as well as it
        // will contain all "interredAssets"...
        updateOnSync: ["assets", "inferredAssets"],

        url: function() {
            return this.isNew() ? RestApi.pathToCollection(RestApi.Collections.PROJECTS) :
                RestApi.pathToSpecificResource(RestApi.Collections.PROJECTS, this.get("id"));
        },

        /**
         * Attaches models to a project and saves it.
         * @param {ImprovedModel | ImprovedModel[]} model
         * @returns {*|Promise}
         */
        attach: function(model) {
            var options = this._syncOptions("attach");
            options.privilege = Permissions.Privileges.CREATE;

            return SyncManager.sync(options, this, model).promise();
        },

        /**
         * Detaches models to a project and saves it.
         * @param {ImprovedModel | ImprovedModel[]} model
         * @returns {*|Promise}
         */
        detach: function(model) {
            var options = this._syncOptions("detach");
            options.privilege = Permissions.Privileges.CREATE;

            return SyncManager.sync(options, this, model).promise();
        },

        /**
         * @private
         *
         * Attaches an asset, or assets, to this project, which gets saved. For convenience specified by model.
         *
         * @param {ImprovedModel|ImprovedModel[]|Backbone.Collection} model The model, or array of models, representing the asset(s).
         *
         * @returns {Promise}
         */
        _attach: function(model) {
            Preconditions.checkArgument(model);

            // Normalize to array
            var models = ProjectAsset.toArray(model),
                assets = this.get("assets");

            // Add the assets for each model
            _.each(models, assets.addModel.bind(assets));

            return this.save();
        },

        /**
         * Detaches an asset, or assets, from this project, which gets saved. For convenience specified by model.
         *
         * @param {ImprovedModel|ImprovedModel[]|Backbone.Collection} model The model, or array of models, representing the asset(s).
         *
         * @returns {Promise}
         */
        _detach: function(model) {
            Preconditions.checkArgument(model);

            // Normalize to array
            var models = ProjectAsset.toArray(model),
                assets = this.get("assets");

            // Add the assets by their respective type
            _.each(models, (model) => {
                assets.removeModel(model);
            });

            return this.save();
        },

        /**
         * Clears/removes any assets by the specified type, e.g. <tt>ProjectAsset.Type.EXPERIENCE</tt>.
         *
         * If no type is specified, all assets are removed, i.e. the asset collection is "reset".
         *
         * @param  {String} type Optional, the asset type, as a <tt>ProjectAsset.Type.XXX</tt> constant value.
         */
        clear: function(type) {
            var assets = this.get("assets");

            if (!type) {
                return assets.reset();
            }

            Preconditions.isString(type);

            assets.remove(this.byType(type));
        },

        /**
         * Convenience method for getting an array of <tt>ProjectAsset</tt>, all with type <tt>ProjectAsset.Type.EXPERIENCE</tt>.
         *
         * @return {ProjectAsset[]} Array of <tt>ProjectAsset</tt>'s.
         * @see  #byType
         */
        experiences: function() {
            return this.byType(ProjectAsset.Type.EXPERIENCE);
        },

        /**
         * Convenience method for getting an array of <tt>ProjectAsset</tt>, all with type <tt>ProjectAsset.Type.GROUP</tt>.
         *
         * @return {ProjectAsset[]} Array of <tt>ProjectAsset</tt>'s.
         * @see  #byType
         */
        groups: function() {
            var breakpoints = this.byType(ProjectAsset.Type.BREAKPOINTGROUP),
                localizations = this.byType(ProjectAsset.Type.LOCALIZATIONGROUP);

            return breakpoints.concat(localizations);
        },

        /**
         * Convenience method for getting an array of "publishables", i.e. experiences and groups.
         *
         * @return {ProjectAsset[]} Array of <tt>ProjectAsset</tt>'s.
         * @see  #byType
         */
        publishables: function() {
            return this.experiences().concat(this.groups());
        },

        /**
         * Convenience method for getting an array of <tt>ProjectAsset</tt>, all with type <tt>ProjectAsset.Type.IMAGE</tt>.
         *
         * @return {ProjectAsset[]} Array of <tt>ProjectAsset</tt>'s.
         * @see  #byType
         */
        images: function() {
            return this.byType(ProjectAsset.Type.IMAGE);
        },

        /**
         * Convenience method for getting an array of <tt>ProjectAsset</tt>, all with type <tt>ProjectAsset.Type.CHANNEL</tt>.
         *
         * @return {ProjectAsset[]} Array of <tt>ProjectAsset</tt>'s.
         * @see  #byType
         */
        channels: function() {
            return this.byType(ProjectAsset.Type.CHANNEL);
        },

        containsGroup: function(resourceId) {
            return this.get("assets").containsGroup(resourceId);
        },

        containsExperience: function(resourceId) {
            return this.get("assets").containsExperience(resourceId);
        },

        containsImage: function(resourceId) {
            return this.get("assets").containsImage(resourceId);
        },

        containsChannel: function(resourceId) {
            return this.get("assets").containsChannel(resourceId);
        },

        containsInferredAsset: function(resourceId) {
            return this.get("inferredAssets").find((asset) => {
                return asset.get("resourceId") === resourceId;
            }) !== undefined;
        },

        /**
         * Returns an array of <tt>ProjectAsset</tt>'s with the specified type.
         *
         * @param  {String} type The asset type, as a <tt>ProjectAsset.Type.XXX</tt> constant value.
         * @return {ProjectAsset[]} Array of <tt>ProjectAsset</tt>'s.
         */
        byType: function(type) {
            Preconditions.checkState(ProjectAsset.validType(type), "Invalid asset type: " + type);

            return this.get("assets").byType(type);
        },

        fromModel: function(model) {
            return ProjectAsset.typeFromModel(model);
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/editor/project/ProjectModel.js